import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiEdit3, FiTag, FiX, FiSave, FiTrash2 } from 'react-icons/fi';
import { Button } from './Button';
import { useI18n } from '../i18n/useI18n';
import { setCustomName } from '../utils/api';

const MAX_CUSTOM_NAME_LENGTH = 32;

export function CustomNameModal({
  open,
  onClose,
  targetType = 'trader',
  targetId,
  currentName = '',
  displayName = '',
  pushToast = () => {},
  onSaved = () => {}
}) {
  const { t } = useI18n();
  const [value, setValue] = useState(currentName || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(currentName || '');
    }
  }, [open, currentName]);

  // 弹窗打开时锁定背景滚动
  useEffect(() => {
    if (!open) return;

    const scrollY = window.scrollY || 0;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const trimmedValue = useMemo(() => value.trim(), [value]);
  const isEmpty = trimmedValue.length === 0;
  const isTooLong = trimmedValue.length > MAX_CUSTOM_NAME_LENGTH;
  const canSave = Boolean(targetId) && !isEmpty && !isTooLong;
  const canClear = Boolean(targetId) && Boolean(currentName);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const response = await setCustomName({
        targetType,
        targetId,
        customName: trimmedValue
      });
      if (response?.code === 0) {
        pushToast(t('customName.saveSuccess'), 'success');
        onSaved(trimmedValue);
        onClose();
      } else {
        pushToast(response?.msg || t('customName.saveFailed'), 'error');
      }
    } catch (error) {
      console.error('[Custom name save]', error);
      pushToast(error.message || t('customName.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!canClear || saving) return;
    setSaving(true);
    try {
      const response = await setCustomName({
        targetType,
        targetId,
        customName: ''
      });
      if (response?.code === 0) {
        pushToast(t('customName.clearSuccess'), 'success');
        onSaved(null);
        onClose();
      } else {
        pushToast(response?.msg || t('customName.clearFailed'), 'error');
      }
    } catch (error) {
      console.error('[Custom name clear]', error);
      pushToast(error.message || t('customName.clearFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-slate-700/60 bg-gradient-to-b from-slate-900/95 to-slate-950/95 backdrop-blur-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sky-500/15 rounded-xl">
              <FiEdit3 className="w-5 h-5 text-sky-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{t('customName.title')}</h3>
              <p className="text-xs text-slate-500 uppercase tracking-wider">{t('customName.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Hint */}
          <p className="text-sm text-slate-400 text-center mb-6">
            {t('customName.hint')}
          </p>

          {/* Target Info */}
          {displayName ? (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/50 mb-6">
              <div className="p-1.5 bg-slate-700/50 rounded-lg">
                <FiTag className="w-4 h-4 text-slate-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 mb-0.5">{t('customName.targetLabel')}</p>
                <p className="text-sm text-slate-200 font-medium truncate">{displayName}</p>
              </div>
            </div>
          ) : null}

          {/* Input */}
          <div className="space-y-3 mb-6">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
              {t('customName.title')}
            </label>
            <div className="relative">
              <input
                className={`w-full rounded-xl bg-slate-950/60 border px-4 py-3.5 text-sm text-slate-200 outline-none transition-all placeholder:text-slate-600 ${
                  isTooLong
                    ? 'border-rose-500/60 focus:border-rose-500/80 focus:ring-2 focus:ring-rose-500/20'
                    : 'border-slate-700/60 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20'
                }`}
                placeholder={t('customName.placeholder')}
                value={value}
                maxLength={MAX_CUSTOM_NAME_LENGTH + 10}
                onChange={(event) => setValue(event.target.value)}
                autoFocus
              />
              {isTooLong && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <span className="text-xs text-rose-400 font-medium">超出限制</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between text-xs px-1">
              <span className={isTooLong ? 'text-rose-400' : 'text-slate-500'}>
                {t('customName.limit', { max: MAX_CUSTOM_NAME_LENGTH })}
              </span>
              <span className={`tabular-nums font-medium ${isTooLong ? 'text-rose-400' : 'text-slate-400'}`}>
                {trimmedValue.length}/{MAX_CUSTOM_NAME_LENGTH}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {canClear ? (
              <>
                <button
                  onClick={handleClear}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 border border-rose-500/30 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiTrash2 className="w-4 h-4" />
                  {saving ? t('customName.saving') : t('customName.clear')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !canSave}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/30 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiSave className="w-4 h-4" />
                  {saving ? t('customName.saving') : t('common.save')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/50 border border-slate-700/50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !canSave}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/30 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiSave className="w-4 h-4" />
                  {saving ? t('customName.saving') : t('common.save')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
